/**
 * useStudyLesson — shared state machine for Puzzle / Typing / Speaking pages.
 *
 * Each study page previously copy-pasted ~50 lines of:
 *   - lessonId → resolve → null guard
 *   - sentence index state + next/jump helpers
 *   - store action bindings (markComplete / recordMistake / bumpStreak / submitGrade / bumpMastery)
 *   - progress percentage
 *   - mistake recording with mode-specific errorType
 *
 * Now it's all here. Pages only own the mode-specific bits (input handling,
 * grading rules, render).
 */
import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { resolveLesson } from '@/lib/data';
import type { Grade } from '@/lib/score';
import { useAppStore } from '@/lib/store';
import type { Course, ErrorType, Lesson, Sentence, StudyMode } from '@/lib/types';

export interface StudyLesson {
  lesson: Lesson;
  course: Course;
  sentence: Sentence;
  index: number;
  total: number;
  progressPct: number;
  /** When `correct=true` the bar shows index+1; otherwise just index. */
  setProgressBoost: (boost: boolean) => void;

  /** Advance to next sentence or return to course detail page if at end. */
  next: () => void;
  /** Jump to a specific sentence index (clamped). */
  goTo: (i: number) => void;

  /** Submit a grade — runs store side effects (XP / combo / mastery / sfx). */
  submitGrade: (g: Grade) => void;
  /** Mark the current sentence completed (idempotent). */
  markComplete: () => void;
  /** Record a mistake on the current sentence. */
  recordMistake: (opts: { errorType: ErrorType; userInput: string }) => void;
  /** Promote the sentence to next mastery tier (called after Perfect / Great). */
  bumpMastery: () => void;
  /** Daily streak nudge — idempotent within a calendar day. */
  bumpStreak: () => void;
}

export interface StudyLessonControls {
  ready: boolean;
  notFoundView: JSX.Element | null;
  study: StudyLesson | null;
}

/**
 * Returns either a ready study state, or a notFoundView fallback to render.
 * Pages use:
 *   const { ready, notFoundView, study } = useStudyLesson('puzzle');
 *   if (!ready) return notFoundView;
 */
export function useStudyLesson(mode: StudyMode): StudyLessonControls {
  const { lessonId = '' } = useParams<{ lessonId: string }>();
  const customs = useAppStore((s) => s.customCourses);
  const resolved = resolveLesson(lessonId, customs);
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [boost, setProgressBoost] = useState(false);

  const markCompleteStore = useAppStore((s) => s.markSentenceComplete);
  const recordMistakeStore = useAppStore((s) => s.recordMistake);
  const bumpStreakStore = useAppStore((s) => s.bumpStreak);
  const submitGradeStore = useAppStore((s) => s.submitGrade);
  const bumpMasteryStore = useAppStore((s) => s.bumpMastery);

  if (resolved === null) {
    return {
      ready: false,
      study: null,
      notFoundView: (
        <div className="text-center py-20">
          <div className="text-6xl mb-4">🔍</div>
          <button
            onClick={() => navigate('/app/courses')}
            className="text-honey-300 hover:text-honey-200 font-arcade text-xs tracking-widest"
          >
            ← BACK TO COURSES
          </button>
        </div>
      ),
    };
  }

  const { lesson, course } = resolved;
  const sentence = lesson.sentences[index]!;
  const total = lesson.sentences.length;
  const progressPct = ((index + (boost ? 1 : 0)) / total) * 100;

  const next = useCallback(() => {
    setProgressBoost(false);
    if (index + 1 < total) setIndex(index + 1);
    else navigate(`/app/courses/${course.id}`);
  }, [index, total, course.id, navigate]);

  const goTo = useCallback((i: number) => {
    setProgressBoost(false);
    setIndex(Math.max(0, Math.min(total - 1, i)));
  }, [total]);

  const submitGrade = useCallback((g: Grade) => {
    submitGradeStore(g);
  }, [submitGradeStore]);

  const markComplete = useCallback(() => {
    markCompleteStore(sentence.id);
  }, [markCompleteStore, sentence.id]);

  const recordMistake = useCallback(
    (opts: { errorType: ErrorType; userInput: string }) => {
      recordMistakeStore({
        sentenceId: sentence.id,
        lessonId: lesson.id,
        courseId: course.id,
        mode,
        errorType: opts.errorType,
        userInput: opts.userInput,
        correct: sentence.en,
      });
    },
    [recordMistakeStore, sentence.id, sentence.en, lesson.id, course.id, mode],
  );

  const bumpMastery = useCallback(() => {
    bumpMasteryStore(sentence.id);
  }, [bumpMasteryStore, sentence.id]);

  const bumpStreak = useCallback(() => {
    bumpStreakStore();
  }, [bumpStreakStore]);

  return {
    ready: true,
    notFoundView: null,
    study: {
      lesson,
      course,
      sentence,
      index,
      total,
      progressPct,
      setProgressBoost,
      next,
      goTo,
      submitGrade,
      markComplete,
      recordMistake,
      bumpMastery,
      bumpStreak,
    },
  };
}
